import {
	AbstractRestSession,
	DocumentManager,
	HttpHeaders,
	HttpMethod,
	RestWebServiceDocument,
	RestWebServiceDocumentManager
} from "@softvision/webpdf-wsclient-9-typescript";
import AbstractContext from "../AbstractContext";
import Exception from "../exception/Exception";
import portalConfiguration from "../configuration/PortalConfiguration";
import {
	AuthProvider,
	Information,
	LoginOptions,
	LoginOptionsInterface,
	PortalAuthTimeout,
	SessionToken,
	SessionTokenInterface,
	SignatureEditProfile,
	UserCredentials
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import {Info} from "@softvision/webpdf-wsclient-9-typescript/lib/session/Info";
import {RestInfo} from "@softvision/webpdf-wsclient-9-typescript/lib/session/rest/RestInfo";
import Timer from "../class/Timer";
import StorageItem from "../StorageItem";
import TokenHandler from "./token/TokenHandler";
import DefaultTokenHandler from "./token/DefaultTokenHandler";
import {HttpRestRequestFactory} from "@softvision/webpdf-wsclient-9-typescript/lib/http/HttpRestRequestFactory";
import OAuthError from "../exception/OAuthError";
import {JWTPayload} from "jose/dist/types/types";
import * as jose from "jose";
import FunctionHelper from "../helper/FunctionHelper";
import DateHelper from "../helper/DateHelper";
import TimeUnit from "../units/TimeUnit";

/**
 * An AbstractSession represents an active connection to the webPDF server.
 */
export default abstract class AbstractSession<C extends AbstractContext<C, S>, S extends AbstractSession<C, S>>
	extends AbstractRestSession<RestWebServiceDocument> {
	protected context: C;
	protected reconnectTries: number;
	protected refreshTimer: Timer;
	protected sessionTimer: Timer;
	protected idleTimer: Timer;
	protected idle: boolean;

	/**
	 * Constructs an {@link AbstractSession} instance
	 */
	constructor(url: URL, context: C, tlsContext?: any) {
		super(url, tlsContext);
		this.reconnectTries = 0;
		this.idle = false;
		this.context = context;

		this.idleTimer = new Timer(() => {
			this.idle = true;
		}, 0);

		this.refreshTimer = new Timer(async () => {
			let refreshThreshold: number = DateHelper.secondsToUnit(
				FunctionHelper.applyValue(
					this.context.info.localAuth?.timeout?.autoRefresh, PortalAuthTimeout.getAutoRefreshDefault()
				), TimeUnit.MILLISECOND
			);

			if (this.getSessionExpiry() - refreshThreshold > refreshThreshold) {
				this.refreshTimer.reset(refreshThreshold);
				return;
			}

			if (!this.idle) {
				try {
					await this.refresh();
				} catch (e: any) {
					await this.handleException(e);
				}

				return;
			}

			this.context.getPage().setState({
				showRefreshDialog: true
			});
		}, 0);

		this.sessionTimer = new Timer(() => {
			this.clearSessionToken();
			window.location.reload();
		}, 0);
	}

	/**
	 * Initializes an {@link AbstractSession}
	 */
	public async initialize(): Promise<void> {
		await this.info.fetch();
		this.context.getPage().setState({
			signatureEditProfile: FunctionHelper.applyValue(
				this.info.get().settings?.signature?.signatureEditProfile, SignatureEditProfile.AdobeRestrictions
			)
		});

		let tokenHandler: TokenHandler = new DefaultTokenHandler(this.context);

		try {
			await tokenHandler.initialize();
		} catch (exception: any) {
			let portalException: Exception = Exception.parseException(exception);
			sessionStorage.setItem(StorageItem.OAUTH_ERROR, JSON.stringify({
				name: portalException.name,
				code: portalException.code,
				description: portalException.message,
				raw: portalException.stack
			} as OAuthError));

			this.clearSessionToken();
			this.context.getPage().setState({
				showLoginDialog: true,
				loginParameter: undefined
			});
		}
	}

	/**
	 * Returns the remaining time till timeout for this session
	 */
	public getSessionExpiry(): number {
		return this.sessionTimer.getRemaining();
	}

	/**
	 * Resets and starts the session timeout
	 */
	public startTimeout(): void {
		let expiresIn: number = DateHelper.secondsToUnit(
			FunctionHelper.applyValue(this.token?.expiresIn, SessionToken.getExpiresInDefault()), TimeUnit.MILLISECOND
		);

		let refreshThreshold: number = DateHelper.secondsToUnit(
			FunctionHelper.applyValue(
				this.context.info.localAuth?.timeout?.autoRefresh, PortalAuthTimeout.getAutoRefreshDefault()
			), TimeUnit.MILLISECOND
		);

		if (FunctionHelper.exists(this.token?.refreshToken) && refreshThreshold > 0) {
			this.refreshTimer.reset(Math.max(expiresIn - refreshThreshold, refreshThreshold));
		} else if (this.refreshTimer.isRunning()) {
			this.refreshTimer.pause();
		}

		this.sessionTimer.reset(expiresIn);
	}

	/**
	 * refresh the token for an existing session
	 */
	public async refresh(token?: string, initial?: boolean): Promise<void> {
		let portalInfo: Information = this.context.info;

		let refreshToken: string | undefined = this.token?.refreshToken;
		if (FunctionHelper.exists(token)) {
			refreshToken = token;
		}

		if (!FunctionHelper.exists(refreshToken)) {
			this.clearSessionToken();
			return;
		}

		let provider: AuthProvider | undefined;
		let providerName: string | null = localStorage.getItem(StorageItem.PROVIDER);
		for (let oauthProvider of FunctionHelper.applyValue(portalInfo.oauth, Information.getOauthDefault())) {
			if (oauthProvider.name === providerName) {
				provider = oauthProvider;
			}
		}

		if (FunctionHelper.exists(provider)) {
			try {
				let params: URLSearchParams = new URLSearchParams();
				params.set("grant_type", "refresh_token");
				params.set("refresh_token", refreshToken!);

				let headers: Headers = new Headers();
				headers.set(HttpHeaders.CONTENT_TYPE, "application/x-www-form-urlencoded");

				let oauthResponse: any = await HttpRestRequestFactory.createInstance(this)
				.buildRequest({
					httpMethod: HttpMethod.POST,
					path: provider!.oidc!.tokenEndpoint,
					payload: params,
					additionalHeaders: headers
				})
				.executeRequest();

				this.token = new SessionToken({
					token: oauthResponse.access_token,
					refreshToken: oauthResponse.refresh_token,
					expiresIn: oauthResponse.expires_in
				} as SessionTokenInterface);

				if (FunctionHelper.exists(oauthResponse.id_token)) {
					sessionStorage.setItem(StorageItem.ID_TOKEN, oauthResponse.id_token);
				}
			} catch (exception: any) {
				let portalException: Exception = Exception.parseException(exception);

				if (FunctionHelper.exists(portalException.originalException?.response?.data)) {
					let data: any = portalException.originalException.response.data;

					if (!FunctionHelper.applyValue(data.error_codes, []).includes(700084)) { // check if refresh token expired
						// handle azure errors
						sessionStorage.setItem(StorageItem.OAUTH_ERROR, JSON.stringify({
							name: FunctionHelper.applyValue(data.error, portalException.name),
							code: FunctionHelper.applyValue(data.error_codes?.pop(), portalException.code),
							description: FunctionHelper.applyValue(data.error_description, portalException.message) +
							FunctionHelper.exists(data.error_uri) ? "([" + data.error_uri + "](" + data.error_uri + "))" : "",
							raw: portalException.stack
						} as OAuthError));
					}
				} else {
					sessionStorage.setItem(StorageItem.OAUTH_ERROR, JSON.stringify({
						name: portalException.name,
						code: portalException.code,
						description: portalException.message,
						raw: portalException.stack
					} as OAuthError));
				}

				this.clearSessionToken();
				window.location.reload();
				return;
			}
		} else {
			try {
				await super.refresh(token);
			} catch (exception: any) {
				let portalException: Exception = Exception.parseException(exception);
				sessionStorage.setItem(StorageItem.OAUTH_ERROR, JSON.stringify({
					name: portalException.name,
					code: portalException.code,
					description: portalException.message,
					raw: portalException.stack
				} as OAuthError));

				this.clearSessionToken();
				window.location.reload();
				return;
			}
		}

		this.storeSessionToken();
		this.startTimeout();

		if (initial) {
			await this.fetchUser();
			await this.documentManager.sync();
		}
	}

	/**
	 * Login into the server with user and password or anonymous
	 */
	public async login(options?: LoginOptions, username?: string, password?: string): Promise<void> {
		try {
			if (!FunctionHelper.exists(options)) {
				options = LoginOptions.fromJson({
					createRefreshToken: true
				} as LoginOptionsInterface);
			}

			await super.login(options, username, password);
			localStorage.removeItem(StorageItem.PROVIDER);
		} catch (exception: any) {
			throw exception;
		}

		this.storeSessionToken();
		this.startTimeout();
	}

	/**
	 * Logout from the server
	 */
	public async close(): Promise<void> {
		try {
			await super.close();
			this.clearSessionToken();
		} catch (exception: any) {
			await this.handleException(exception);
		}
	}

	/**
	 * Store the current session tokens to local and session storage.
	 */
	private storeSessionToken() {
		localStorage.setItem(StorageItem.REFRESH_TOKEN_CREATION, new Date().getTime().toString());
		localStorage.setItem(StorageItem.REFRESH_TOKEN, this.token!.refreshToken!);
		sessionStorage.setItem(StorageItem.ACCESS_TOKEN, this.token!.token!);
	}

	/**
	 * Clears the current session tokens from local and session storage.
	 */
	public clearSessionToken(): void {
		this.token = undefined;
		AbstractSession.clearSessionStorage();
	}

	public static clearSessionStorage(): void {
		localStorage.removeItem(StorageItem.REFRESH_TOKEN);
		localStorage.removeItem(StorageItem.REFRESH_TOKEN_CREATION);
		localStorage.removeItem(StorageItem.PROVIDER);
		sessionStorage.removeItem(StorageItem.ACCESS_TOKEN);
		sessionStorage.removeItem(StorageItem.ID_TOKEN);
	}

	/**
	 * reconnect to the server after connection loss.
	 */
	public async reconnectServer(): Promise<void> {
		this.reconnectTries++;

		this.context.getPage().setState({
			showReconnectDialog: true,
			reconnectDialogTries: this.reconnectTries
		});

		if (this.reconnectTries >= portalConfiguration.reconnectTries) {
			window.document.location.reload();
			return;
		}

		window.setTimeout(async () => {
			try {
				await this.context.fetchInfo();
				window.document.location.reload();
			} catch (exception: any) {
				await this.reconnectServer();
			}
		}, portalConfiguration.reconnectTimeout);
	};

	/**
	 * Creates a new {@link DocumentManager} instance.
	 */
	protected createDocumentManager(): DocumentManager<RestWebServiceDocument> {
		return new RestWebServiceDocumentManager(this);
	}

	/**
	 * Creates a new {@link Info} instance.
	 */
	protected createInfo(): Info {
		return new RestInfo(this);
	}

	/**
	 * resets the idle state of a session and starts hte idle timer. This is used for automated refreshing of the
	 * session.
	 */
	public resetIdle(): void {
		this.idle = false;

		let idleTimeout: number = DateHelper.secondsToUnit(
			FunctionHelper.applyValue(
				this.context.info.localAuth?.timeout?.inactivity, PortalAuthTimeout.getInactivityDefault()
			), TimeUnit.MILLISECOND
		);

		if (idleTimeout > 0) {
			this.idleTimer.reset(idleTimeout);
		} else {
			this.idleTimer.pause();
		}
	}

	/**
	 * Set the current user with its {@link UserCredentials} for this session.
	 */
	public setUser(user: UserCredentials | undefined): void {
		this.user = user;
	}

	/**
	 * Sets the {@link SessionToken} for this session.
	 */
	public setToken(token: SessionToken): void {
		this.token = token;
	}

	/**
	 * Fetch the {@link UserCredentials} for the currently active session from the server.
	 */
	public async fetchUser(): Promise<UserCredentials | undefined> {
		let user: UserCredentials | undefined = await super.fetchUser();
		let idToken: string | null = sessionStorage.getItem(StorageItem.ID_TOKEN);

		if (FunctionHelper.exists(user) && idToken !== null && user!.userName === "") {
			let tokenPayload: JWTPayload = jose.decodeJwt(idToken);
			let username: string = "";

			if (FunctionHelper.exists(tokenPayload.preferred_username)) {
				username = tokenPayload.preferred_username as string;
			} else if (FunctionHelper.exists(tokenPayload.email)) {
				username = tokenPayload.email as string;
			}

			user!.userName = username;
		}

		return user;
	}

	/**
	 * Handle {@link Exception}s for this session and its webservices.
	 */
	abstract handleException(exception: Error, showMessage?: boolean): Promise<Exception>;
}